import { differenceInCalendarDays, parseISO, startOfDay } from 'date-fns';
import { inquiryNeedsReply } from './partnerListUtils';

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = parseISO(raw.includes('T') ? raw : `${raw}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getOpsToday = (now = new Date()) => startOfDay(now);

export const isTripTravelingNow = (trip, today = getOpsToday()) => {
  const start = toDate(trip?.start_date);
  const end = toDate(trip?.end_date);
  if (!start || !end) return false;
  return start <= today && end >= today;
};

export const isTripDepartingSoon = (trip, today = getOpsToday(), withinDays = 14) => {
  const start = toDate(trip?.start_date);
  if (!start || start < today) return false;
  return differenceInCalendarDays(start, today) <= withinDays;
};

export const clientNameFromTrip = (trip) => {
  if (trip?.owner_first_name && trip?.owner_last_name) {
    return `${trip.owner_first_name} ${trip.owner_last_name}`;
  }
  if (trip?.creator_first_name && trip?.creator_last_name) {
    return `${trip.creator_first_name} ${trip.creator_last_name}`;
  }
  return (
    trip?.owner_username
    || trip?.owner_email
    || trip?.creator_name
    || trip?.creator_email
    || trip?.client_name
    || 'Client'
  );
};

export const tripDestinationLabel = (trip) => {
  if (trip?.destination) return String(trip.destination);
  const locations = trip?.locations;
  if (Array.isArray(locations) && locations.length) {
    const first = locations[0];
    return first?.name || first?.label || String(first);
  }
  if (typeof locations === 'string') {
    try {
      const parsed = JSON.parse(locations);
      if (Array.isArray(parsed) && parsed[0]) {
        return parsed[0].name || parsed[0].label || String(parsed[0]);
      }
    } catch {
      return locations;
    }
  }
  return '';
};

export const tripTimingLabelKey = (trip, today = getOpsToday()) => {
  const start = toDate(trip?.start_date);
  const end = toDate(trip?.end_date);
  if (!start || !end) return { key: 'timingUnknown', values: {} };
  if (start <= today && end >= today) {
    const dayNum = differenceInCalendarDays(today, start) + 1;
    return { key: 'timingDay', values: { day: dayNum } };
  }
  const daysUntil = differenceInCalendarDays(start, today);
  if (daysUntil === 1) return { key: 'timingTomorrow', values: {} };
  if (daysUntil > 1) return { key: 'timingInDays', values: { count: daysUntil } };
  return { key: 'timingPast', values: {} };
};

export const selectTravelingTrips = (trips = [], today = getOpsToday()) => (
  trips
    .filter((trip) => isTripTravelingNow(trip, today))
    .sort((a, b) => String(a.start_date || '').localeCompare(String(b.start_date || '')))
);

export const selectUpcomingDepartures = (trips = [], today = getOpsToday(), withinDays = 14) => (
  trips
    .filter((trip) => isTripDepartingSoon(trip, today, withinDays))
    .sort((a, b) => String(a.start_date || '').localeCompare(String(b.start_date || '')))
);

export const selectApprovalsWaiting = (bookings = []) => (
  bookings.filter((booking) => booking?.status === 'pending')
);

export const selectClientMessagesNeedingReply = (inquiries = []) => (
  inquiries.filter((inquiry) => inquiryNeedsReply(inquiry))
);

export const selectActiveTripRows = (trips = [], today = getOpsToday()) => {
  const traveling = selectTravelingTrips(trips, today);
  const upcoming = selectUpcomingDepartures(trips, today, 14);
  const seen = new Set();
  const rows = [];
  [...traveling, ...upcoming].forEach((trip) => {
    if (!trip?.id || seen.has(trip.id)) return;
    seen.add(trip.id);
    rows.push(trip);
  });
  return rows;
};

export const buildPartnerOpsHomeModel = ({
  trips = [],
  bookings = [],
  inquiries = [],
  flightAlerts = 0,
  now = new Date(),
} = {}) => {
  const today = getOpsToday(now);
  const travelingTrips = selectTravelingTrips(trips, today);
  const approvals = selectApprovalsWaiting(bookings);
  const messages = selectClientMessagesNeedingReply(inquiries);
  const departures = selectUpcomingDepartures(trips, today, 14);
  const activeTrips = selectActiveTripRows(trips, today);
  const flightAlertCount = Number(flightAlerts) || 0;

  return {
    today,
    clientsTraveling: travelingTrips.length,
    approvalsWaiting: approvals.length,
    clientMessages: messages.length,
    upcomingDepartures: departures.length,
    flightAlerts: flightAlertCount,
    travelingTrips,
    activeTrips,
    approvals,
    messages,
    departures,
  };
};
